/**
 * `<NextStepsSection/>` — the lead's right panel content (#1921, R3 of
 * `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md`).
 *
 * **Why the lead opens the panel:** to know what still needs them on this
 * roster without reading the grid. **What they see first:** the one most
 * urgent thing, as a sentence with a count. **What they do straight away:**
 * click the row and land where the action is.
 *
 * One row per kind of step, in the fixed urgency order of
 * `nextSteps.ts` (it never reshuffles while open): the sentence (it carries
 * its count), a quieter second line where there is one, a chevron. The row
 * is a link to where the action is — the first short day's ladder, the
 * team page's review queue on that period, the Swaps board on its held
 * rows, the roster's Publish button, *Fixed placements that can't happen*,
 * the team page's waiting-calls door or Roster. Following a row closes the drawer (the
 * page, and the panel the row opens, are what the lead works next) and,
 * for an action that is a control on the page, brings it into view and
 * focuses it.
 *
 * No heading of its own: the drawer's title already says *Next steps*.
 * Empty: one line — *Nothing needs you on this roster.* (on a team page,
 * *… on this team.*).
 */
import { ChevronRight } from "lucide-react";
import type { MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { cn } from "../../lib/cn";
import { setPersistentPanelOpen } from "../../lib/persistentPanel";
import { useCascadeOnce } from "../../lib/useCascadeOnce";
import { Skeleton } from "../ui";
import { NEXT_STEPS_EMPTY, type NextStepRow } from "./nextSteps";
import { useNextSteps } from "./useNextSteps";

export function NextStepsSection() {
  const { scope, rows, loading } = useNextSteps();
  if (scope === null) return null;
  return <NextStepsList scope={scope} rows={rows} loading={loading} />;
}

export interface NextStepsListProps {
  scope: "cycle" | "team";
  rows: readonly NextStepRow[];
  loading?: boolean;
}

/** The presentational list — `NextStepsSection` feeds it from the route. */
export function NextStepsList({ scope, rows, loading = false }: NextStepsListProps) {
  const go = useGoToStep();
  // #2172 — the steps cascade in as the panel opens (each opening mounts
  // the list afresh); a later recount never replays it.
  const cascade = useCascadeOnce(rows.length > 0);

  if (rows.length === 0) {
    return (
      <section aria-label="Next steps" data-testid="next-steps" className="px-1 py-1">
        {loading ? (
          <div className="flex flex-col gap-2" data-testid="next-steps-loading">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : (
          <p className="px-2 py-2 text-sm text-fg-muted" data-testid="next-steps-empty">
            {NEXT_STEPS_EMPTY[scope]}
          </p>
        )}
      </section>
    );
  }

  return (
    <section aria-label="Next steps" data-testid="next-steps" className="px-1 py-1">
      <ul className="flex flex-col divide-y divide-border" data-cascade={cascade}>
        {rows.map((row, index) => (
          <li
            key={row.kind}
            data-testid={`next-steps-row-${row.kind}`}
            data-count={row.count}
            className="flex flex-col py-1"
          >
            <Link
              to={row.to}
              onClick={(event) => go(event, row.to, row.focus)}
              className={cn(
                "group flex items-center gap-2 rounded-md px-2 py-2 text-left",
                "hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              )}
              data-testid={`next-steps-link-${row.kind}`}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span
                  className={cn(
                    "text-sm leading-snug text-fg",
                    // The one most urgent thing reads first.
                    index === 0 && "font-semibold",
                  )}
                >
                  {row.line}
                </span>
                {row.secondary ? (
                  <span className="text-xs text-fg-muted">{row.secondary}</span>
                ) : null}
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-fg-subtle group-hover:text-fg"
                aria-hidden="true"
              />
            </Link>
            {row.aside ? (
              <Link
                to={row.aside.to}
                onClick={(event) => go(event, row.aside?.to ?? row.to, row.aside?.focus)}
                className="ml-2 inline-flex w-fit items-center gap-0.5 rounded px-1 pb-1 text-xs text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                data-testid={`next-steps-aside-${row.kind}`}
              >
                {row.aside.line}
                <ChevronRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The row click: close the drawer, navigate, and — for a control on the
 * page — bring it into view. A modified click (new tab, …) is left to the
 * browser.
 *
 * A `?surface=` link to the surface the URL already names would not re-open
 * it (the workspace opens a surface when the hint CHANGES, and closing one
 * leaves the hint in the URL), so that case drops the hint first and
 * navigates on the next tick.
 */
function useGoToStep() {
  const navigate = useNavigate();
  const location = useLocation();
  return (event: MouseEvent<HTMLAnchorElement>, to: string, focus: string | undefined) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    setPersistentPanelOpen(false);

    const target = new URL(to, "http://tarii.local");
    const here = new URLSearchParams(location.search);
    const surface = target.searchParams.get("surface");
    const samePath = target.pathname === location.pathname;
    if (surface && samePath && here.get("surface") === surface) {
      here.delete("surface");
      const rest = here.toString();
      navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
      window.setTimeout(() => navigate(to), 0);
    } else if (!(samePath && target.search === location.search)) {
      navigate(to);
    }
    if (focus) bringIntoView(focus);
  };
}

/**
 * Scroll the element `selector` names into view and focus it, once it is on
 * the page (it may arrive with the next route's data — retried for ~3 s).
 * A collapsed section is opened first. The first try waits a beat so the
 * drawer's own focus return (to its handle) has already run.
 */
function bringIntoView(selector: string): void {
  let tries = 0;
  const attempt = () => {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) {
      tries += 1;
      if (tries < 30) window.setTimeout(attempt, 100);
      return;
    }
    const collapsible = el.matches("[data-collapsed]")
      ? el
      : el.querySelector<HTMLElement>("[data-collapsed]");
    const toggle = collapsible?.querySelector<HTMLButtonElement>(":scope > header > button");
    if (collapsible?.getAttribute("data-collapsed") === "true") toggle?.click();
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    const focusable = el.matches("button, a[href], [tabindex]") ? el : (toggle ?? null);
    focusable?.focus({ preventScroll: true });
  };
  window.setTimeout(attempt, 60);
}
